/**
 * Google Drive Repository
 *
 * Stores all entity data in Google Drive for Pro users.
 * Uses a single encrypted profile file with local cache for performance.
 *
 * Architecture (from GOOGLE_DRIVE_STORAGE_ARCHITECTURE.md):
 *
 * Folder Structure:
 * LucidDock/                              (root folder, visible to user)
 * ├── .luciddock/                         (hidden config folder)
 * │   ├── profile.encrypted.json          (encrypted profile data - ALL entities)
 * │   └── profile.meta.json               (version, lastSync, checksum)
 * │
 * └── {Profile-Name}/                     (user-browsable content folder)
 *     ├── Addresses/
 *     │   └── {Address-Label}/
 *     │       ├── Photos/
 *     │       └── Documents/
 *     ├── Vehicles/
 *     │   └── {Year-Make-Model}/
 *     ├── Insurance/
 *     ├── Healthcare/
 *     ├── Financial/
 *     └── Profile/
 *         └── avatar.jpg
 *
 * Security:
 * - Profile data encrypted with AES-256-GCM
 * - Key derived from Google User ID + App Secret using PBKDF2 (100,000 iterations)
 * - Photos and documents are NOT encrypted (user can browse in Drive)
 */

import type { IContentRepository, SyncStatus } from './IContentRepository';
import type { Address, Vehicle, InsurancePolicy } from '../../types/profile';
import type { HealthProvider } from '../../types/health';
import type { FinancialInstitution } from '../../types/financial';
import type { Portal } from '../../types/portal';
import { googleDriveService } from '../googleDriveService';
import { encryptionService, type EncryptedPayload } from '../encryption/encryptionService';

// Google Drive API constants
const GOOGLE_DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const GOOGLE_DRIVE_UPLOAD_API = 'https://www.googleapis.com/upload/drive/v3';
const MIME_TYPES = {
  FOLDER: 'application/vnd.google-apps.folder',
  JSON: 'application/json',
};

const APP_SECRET = import.meta.env.VITE_ENCRYPTION_SECRET || 'luciddock-default-secret-change-in-production';

// File names
const PROFILE_ENCRYPTED_FILE = 'profile.encrypted.json';
const PROFILE_META_FILE = 'profile.meta.json';

/**
 * Profile data structure stored in encrypted file
 */
interface ProfileData {
  addresses: Address[];
  vehicles: Vehicle[];
  health: HealthProvider[];
  financial: FinancialInstitution[];
  insurance: InsurancePolicy[];
  portals: Portal[];
}

/**
 * Metadata for sync tracking
 */
interface ProfileMeta {
  version: number;
  lastSyncedAt: string;
  checksum: string;
  deviceId: string;
  schemaVersion: number;
}

/**
 * Google Drive Repository Implementation
 *
 * Following the architecture:
 * - Single encrypted profile file for all entity data
 * - User-browsable folders for photos/documents
 * - AES-256-GCM encryption with PBKDF2 key derivation
 */
export class GoogleDriveRepository implements IContentRepository {
  readonly type = 'drive' as const;

  private ready: boolean = false;
  private rootFolderId: string | null = null;
  private configFolderId: string | null = null; // .luciddock folder
  private profileFolderId: string | null = null; // {Profile-Name} folder
  private folderCache: Map<string, string> = new Map();
  // Serializes concurrent ensureFolder calls for the same path to prevent duplicate folders
  private folderLocks: Map<string, Promise<string | null>> = new Map();

  // Initialization lock to prevent concurrent initialization
  private initializationPromise: Promise<void> | null = null;
  private isInitializing: boolean = false;

  // Profile info
  private userId: string = '';
  private profileName: string = '';

  // Encrypted profile file IDs
  private encryptedFileId: string | null = null;
  private metaFileId: string | null = null;

  // Local cache (in-memory)
  private cache: ProfileData = {
    addresses: [],
    vehicles: [],
    health: [],
    financial: [],
    insurance: [],
    portals: [],
  };

  private meta: ProfileMeta = {
    version: 0,
    lastSyncedAt: '',
    checksum: '',
    deviceId: this.generateDeviceId(),
    schemaVersion: 1,
  };

  private isDirty: boolean = false;
  private saveTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Generate a unique device ID for conflict resolution
   */
  private generateDeviceId(): string {
    let deviceId = localStorage.getItem('lucidDock_deviceId');
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('lucidDock_deviceId', deviceId);
    }
    return deviceId;
  }

  /**
   * Get user ID for encryption key derivation.
   * For family members, this.userId is set to the Pilot's email (ownerEmail)
   * which was used to encrypt the data. Must take priority over localStorage
   * (which stores the family member's own email from their Drive connection).
   */
  private getUserId(): string {
    return this.userId || localStorage.getItem('lucidDock_googleDrive_userEmail') || '';
  }

  /**
   * Check if token is valid and refresh if needed
   * Delegates to googleDriveService which handles token refresh automatically
   */
  private async ensureValidToken(): Promise<string | null> {
    // Use googleDriveService's getValidAccessToken which handles refresh
    return await googleDriveService.getValidAccessToken();
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  async initialize(_profileId: string, profileName: string): Promise<void> {
    // If already ready, no need to initialize again
    if (this.ready) {
      return;
    }

    // If currently initializing, wait for the existing initialization to complete
    if (this.isInitializing && this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    // Set lock and start initialization
    this.isInitializing = true;
    this.initializationPromise = this.doInitialize(_profileId, profileName);

    try {
      await this.initializationPromise;
    } finally {
      this.isInitializing = false;
      this.initializationPromise = null;
    }
  }

  /**
   * Internal initialization logic - should only be called via initialize()
   */
  private async doInitialize(_profileId: string, profileName: string): Promise<void> {
    this.userId = this.getUserId();
    this.profileName = profileName;

    const token = await this.ensureValidToken();
    if (!token) {
      console.error('GoogleDriveRepository: No valid access token');
      return;
    }

    // Get or create root folder (LucidDock)
    this.rootFolderId = await this.ensureFolder('root', 'LucidDock');
    if (!this.rootFolderId) {
      console.error('GoogleDriveRepository: Failed to create root folder');
      return;
    }

    // Create hidden config folder (.luciddock)
    this.configFolderId = await this.ensureFolder(this.rootFolderId, '.luciddock');
    if (!this.configFolderId) {
      console.error('GoogleDriveRepository: Failed to create .luciddock folder');
      return;
    }

    // Create user-browsable profile folder ({Profile-Name})
    // Note: Category subfolders (Addresses, Vehicles, etc.) are created lazily
    // when the user uploads their first file to that category
    this.profileFolderId = await this.ensureFolder(this.rootFolderId, profileName);
    if (!this.profileFolderId) {
      console.error('GoogleDriveRepository: Failed to create profile folder');
      return;
    }

    // Load encrypted profile data
    await this.loadProfileFromDrive();

    this.ready = true;
  }

  isReady(): boolean {
    return this.ready;
  }

  // ============================================================================
  // FAMILY MEMBER INITIALIZATION (shared Drive access)
  // ============================================================================

  /**
   * Initialize for family member access using a shared Google Drive folder.
   * Family members access the Pilot's LucidDock folder via their own Google token.
   * Decryption uses the Pilot's email (ownerEmail) as the encryption key.
   *
   * @param _profileId - Profile ID (for interface consistency)
   * @param profileName - Profile name (used to find the profile subfolder)
   * @param rootFolderId - The Pilot's LucidDock root folder ID (from Firestore)
   * @param ownerEmail - The Pilot's Google email (used for decryption key derivation)
   */
  async initializeAsFamily(
    _profileId: string,
    profileName: string,
    rootFolderId: string,
    ownerEmail: string
  ): Promise<void> {
    if (this.ready) return;

    if (this.isInitializing && this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    this.isInitializing = true;
    this.initializationPromise = this.doInitializeAsFamily(profileName, rootFolderId, ownerEmail);

    try {
      await this.initializationPromise;
    } finally {
      this.isInitializing = false;
      this.initializationPromise = null;
    }
  }

  /**
   * Internal family initialization — search-only, no folder creation.
   */
  private async doInitializeAsFamily(
    profileName: string,
    rootFolderId: string,
    ownerEmail: string
  ): Promise<void> {
    // Use Pilot's email for decryption (not the current user's)
    this.userId = ownerEmail;
    this.profileName = profileName;

    const token = await this.ensureValidToken();
    if (!token) {
      console.error('GoogleDriveRepository: No valid access token for family access');
      return;
    }

    // Use known root folder ID (stored in Firestore by Pilot)
    this.rootFolderId = rootFolderId;

    // Find .luciddock config folder — first try as child of root folder.
    // If the root folder isn't directly shared (drive.file fallback shares children
    // individually), search for the shared .luciddock folder without parent constraint.
    this.configFolderId = await this.findFolder(rootFolderId, '.luciddock');
    if (!this.configFolderId) {
      this.configFolderId = await this.findSharedFolder('.luciddock');
    }
    if (!this.configFolderId) {
      console.error('GoogleDriveRepository: .luciddock folder not found in shared Drive');
      return;
    }

    // Find profile folder — same fallback strategy
    this.profileFolderId = await this.findFolder(rootFolderId, profileName);
    if (!this.profileFolderId) {
      this.profileFolderId = await this.findSharedFolder(profileName);
    }
    if (!this.profileFolderId) {
      console.error('GoogleDriveRepository: Profile folder not found in shared Drive');
      return;
    }

    // Load and decrypt profile data using Pilot's encryption key
    await this.loadProfileFromDrive();

    this.ready = true;
  }

  /**
   * Search for an existing folder by name within a parent (no creation).
   * Used by family members who read from shared folders but never create.
   */
  private async findFolder(parentId: string, folderName: string): Promise<string | null> {
    const cacheKey = `${parentId}/${folderName}`;
    if (this.folderCache.has(cacheKey)) {
      return this.folderCache.get(cacheKey) || null;
    }

    const token = await this.ensureValidToken();
    if (!token) return null;

    try {
      const safeName = folderName.replace(/'/g, "\\'");
      const parentQuery = `'${parentId}' in parents`;
      const response = await fetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `name='${safeName}' and mimeType='${MIME_TYPES.FOLDER}' and ${parentQuery} and trashed=false`,
            fields: 'files(id,name)',
            spaces: 'drive',
          }),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) return null;

      const data = await response.json();
      if (data.files && data.files.length > 0) {
        const folderId = data.files[0].id;
        this.folderCache.set(cacheKey, folderId);
        return folderId;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Search for a shared folder by name (no parent constraint).
   * Fallback for family members when the root folder isn't directly shared
   * (drive.file scope limitation causes child-level sharing instead of root-level).
   */
  private async findSharedFolder(folderName: string): Promise<string | null> {
    const token = await this.ensureValidToken();
    if (!token) return null;

    try {
      const safeName = folderName.replace(/'/g, "\\'");
      const response = await fetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `name='${safeName}' and mimeType='${MIME_TYPES.FOLDER}' and sharedWithMe=true and trashed=false`,
            fields: 'files(id,name)',
            spaces: 'drive',
          }),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) return null;

      const data = await response.json();
      if (data.files && data.files.length > 0) {
        return data.files[0].id;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Reset the repository state so it re-initializes on next `initialize()` call.
   * Must be called when Drive is disconnected or reconnected.
   */
  reset(): void {
    this.ready = false;
    this.rootFolderId = null;
    this.configFolderId = null;
    this.profileFolderId = null;
    this.encryptedFileId = null;
    this.metaFileId = null;
    this.folderCache.clear();
    this.folderLocks.clear();
    this.cache = { addresses: [], vehicles: [], health: [], financial: [], insurance: [], portals: [] };
    this.isDirty = false;
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }

  // ============================================================================
  // FOLDER OPERATIONS
  // ============================================================================

  private async ensureFolder(parentId: string, folderName: string): Promise<string | null> {
    const cacheKey = `${parentId}/${folderName}`;

    // 1. Cache hit — instant return
    if (this.folderCache.has(cacheKey)) {
      return this.folderCache.get(cacheKey) || null;
    }

    // 2. Another call already in-flight for this folder — wait for it (prevents duplicates)
    const pending = this.folderLocks.get(cacheKey);
    if (pending) return pending;

    // 3. First caller — create promise, store as lock
    const promise = this._ensureFolderImpl(parentId, folderName, cacheKey);
    this.folderLocks.set(cacheKey, promise);
    try {
      return await promise;
    } finally {
      this.folderLocks.delete(cacheKey); // Always cleanup, even on error
    }
  }

  /** Search-then-create implementation (called under lock) */
  private async _ensureFolderImpl(parentId: string, folderName: string, cacheKey: string): Promise<string | null> {
    const token = await this.ensureValidToken();
    if (!token) return null;

    try {
      // Search for existing folder (escape single quotes in name)
      const safeName = folderName.replace(/'/g, "\\'");
      const parentQuery = parentId === 'root' ? "'root' in parents" : `'${parentId}' in parents`;
      const searchResponse = await fetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `name='${safeName}' and mimeType='${MIME_TYPES.FOLDER}' and ${parentQuery} and trashed=false`,
            fields: 'files(id,name)',
            spaces: 'drive',
          }),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Bail immediately on auth errors — don't cascade to create
      if (searchResponse.status === 401 || searchResponse.status === 403) {
        googleDriveService.clearStoredToken();
        return null;
      }

      if (searchResponse.ok) {
        const data = await searchResponse.json();
        if (data.files && data.files.length > 0) {
          const folderId = data.files[0].id;
          this.folderCache.set(cacheKey, folderId);
          return folderId;
        }
      }

      // Create new folder
      const createResponse = await fetch(`${GOOGLE_DRIVE_API_BASE}/files`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: folderName,
          mimeType: MIME_TYPES.FOLDER,
          parents: parentId === 'root' ? undefined : [parentId],
        }),
      });

      if (createResponse.ok) {
        const folder = await createResponse.json();
        this.folderCache.set(cacheKey, folder.id);
        return folder.id;
      }

      return null;
    } catch (error) {
      console.error('GoogleDriveRepository: Error ensuring folder:', error);
      return null;
    }
  }

  /**
   * Get folder ID for user-browsable content (Photos/Documents)
   */
  async getContentFolderId(entityType: 'Addresses' | 'Vehicles' | 'Insurance' | 'Healthcare' | 'Financial', entityLabel?: string): Promise<string | null> {
    if (!this.profileFolderId) return null;

    // Get the entity type folder
    const entityFolderKey = `${this.profileFolderId}/${entityType}`;
    let entityFolderId = this.folderCache.get(entityFolderKey);

    if (!entityFolderId) {
      entityFolderId = await this.ensureFolder(this.profileFolderId, entityType) || undefined;
    }

    if (!entityFolderId || !entityLabel) return entityFolderId || null;

    // Get the specific entity folder (e.g., "123 Main Street")
    return await this.ensureFolder(entityFolderId, entityLabel);
  }

  // ============================================================================
  // FOLDER REPAIR (handles externally deleted folders)
  // ============================================================================

  /**
   * Re-create the folder structure when folders have been deleted externally
   * (e.g., user deleted LucidDock or .luciddock from Google Drive).
   * Clears stale cached IDs and re-runs ensureFolder chain.
   */
  private async repairFolderStructure(): Promise<boolean> {
    if (!this.profileName) return false;

    // Clear stale folder cache and file IDs — they point to deleted resources
    this.folderCache.clear();
    this.encryptedFileId = null;
    this.metaFileId = null;

    // Re-create folder chain: root → .luciddock → profile
    this.rootFolderId = await this.ensureFolder('root', 'LucidDock');
    if (!this.rootFolderId) return false;

    this.configFolderId = await this.ensureFolder(this.rootFolderId, '.luciddock');
    if (!this.configFolderId) return false;

    this.profileFolderId = await this.ensureFolder(this.rootFolderId, this.profileName);
    return true;
  }

  // ============================================================================
  // ENCRYPTED PROFILE FILE OPERATIONS
  // ============================================================================

  /**
   * Load profile data from encrypted file in Drive
   */
  private async loadProfileFromDrive(): Promise<void> {
    if (!this.configFolderId) return;

    const token = await this.ensureValidToken();
    if (!token) return;

    try {
      // Find encrypted profile file
      this.encryptedFileId = await this.findFile(this.configFolderId, PROFILE_ENCRYPTED_FILE);
      this.metaFileId = await this.findFile(this.configFolderId, PROFILE_META_FILE);

      if (!this.encryptedFileId) {
        // No existing profile file - starting fresh
        return;
      }

      // Load meta file first
      if (this.metaFileId) {
        const metaContent = await this.downloadFile(this.metaFileId);
        if (metaContent) {
          this.meta = JSON.parse(metaContent);
        }
      }

      // Load and decrypt profile data
      const encryptedContent = await this.downloadFile(this.encryptedFileId);
      if (!encryptedContent) {
        console.error('GoogleDriveRepository: Failed to download encrypted profile');
        return;
      }

      const encryptedPayload: EncryptedPayload = JSON.parse(encryptedContent);
      const decryptResult = await encryptionService.decryptJSON<ProfileData>(
        encryptedPayload,
        this.getUserId(),
        APP_SECRET
      );

      if (decryptResult.success && decryptResult.data) {
        this.cache = decryptResult.data;
      } else {
        console.error('GoogleDriveRepository: Decryption failed:', decryptResult.error);
      }
    } catch (error) {
      console.error('GoogleDriveRepository: Error loading profile:', error);
    }
  }

  /**
   * Save profile data to encrypted file in Drive.
   * On failure (e.g. deleted folders), attempts folder repair and retries once.
   */
  private async saveProfileToDrive(): Promise<boolean> {
    const success = await this._attemptSave();
    if (success) return true;

    // First attempt failed — try repairing folder structure and retry once
    console.warn('GoogleDriveRepository: Save failed — repairing folder structure and retrying...');
    const repaired = await this.repairFolderStructure();
    if (!repaired) {
      console.error('GoogleDriveRepository: Folder repair failed — cannot save');
      return false;
    }

    return await this._attemptSave();
  }

  /**
   * Single save attempt — encrypt and upload profile + meta files.
   */
  private async _attemptSave(): Promise<boolean> {
    if (!this.configFolderId) {
      console.error('GoogleDriveRepository: Cannot save - configFolderId not set. Was initialize() called?');
      return false;
    }

    const token = await this.ensureValidToken();
    if (!token) {
      console.error('GoogleDriveRepository: Cannot save - no valid access token. Token may have expired.');
      return false;
    }

    try {
      // Encrypt profile data
      const encryptResult = await encryptionService.encrypt(
        this.cache,
        this.getUserId(),
        APP_SECRET
      );

      if (!encryptResult.success || !encryptResult.payload) {
        console.error('GoogleDriveRepository: Failed to encrypt profile:', encryptResult.error);
        return false;
      }

      // Generate checksum
      const checksum = await encryptionService.generateChecksum(JSON.stringify(this.cache));

      // Update meta
      this.meta.version += 1;
      this.meta.lastSyncedAt = new Date().toISOString();
      this.meta.checksum = checksum;

      // Upload encrypted profile
      const encryptedContent = JSON.stringify(encryptResult.payload, null, 2);
      const newEncryptedId = await this.uploadFile(
        this.configFolderId,
        PROFILE_ENCRYPTED_FILE,
        encryptedContent,
        this.encryptedFileId || undefined
      );
      if (!newEncryptedId) return false;
      this.encryptedFileId = newEncryptedId;

      // Upload meta file
      const metaContent = JSON.stringify(this.meta, null, 2);
      const newMetaId = await this.uploadFile(
        this.configFolderId,
        PROFILE_META_FILE,
        metaContent,
        this.metaFileId || undefined
      );
      if (!newMetaId) return false;
      this.metaFileId = newMetaId;

      this.isDirty = false;
      return true;
    } catch (error) {
      console.error('GoogleDriveRepository: Error saving profile:', error);
      return false;
    }
  }

  /**
   * Debounced save - collects changes and saves after a short delay
   */
  private scheduleSave(): void {
    this.isDirty = true;

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    // Save after 1 second of no changes
    this.saveTimeout = setTimeout(async () => {
      const success = await this.saveProfileToDrive();
      if (!success) {
        console.error('GoogleDriveRepository: SAVE FAILED - data may not be persisted!');
        console.error('GoogleDriveRepository: configFolderId:', this.configFolderId);
        console.error('GoogleDriveRepository: ready:', this.ready);
        // Dispatch custom event so UI can handle the error
        window.dispatchEvent(new CustomEvent('luciddock:save-failed', {
          detail: { message: 'Failed to save to Google Drive. Please check your connection.' }
        }));
      }
    }, 1000);
  }

  /**
   * Force immediate save
   */
  async forceSave(): Promise<boolean> {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    return await this.saveProfileToDrive();
  }

  // ============================================================================
  // FILE OPERATIONS
  // ============================================================================

  private async findFile(folderId: string, fileName: string): Promise<string | null> {
    const token = await this.ensureValidToken();
    if (!token) return null;

    try {
      const response = await fetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `name='${fileName}' and '${folderId}' in parents and trashed=false`,
            fields: 'files(id)',
            spaces: 'drive',
          }),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.files && data.files.length > 0) {
          return data.files[0].id;
        }
      }

      return null;
    } catch (error) {
      console.error('GoogleDriveRepository: Error finding file:', error);
      return null;
    }
  }

  private async downloadFile(fileId: string): Promise<string | null> {
    const token = await this.ensureValidToken();
    if (!token) return null;

    try {
      const response = await fetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}?alt=media`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        return await response.text();
      }

      return null;
    } catch (error) {
      console.error('GoogleDriveRepository: Error downloading file:', error);
      return null;
    }
  }

  private async uploadFile(folderId: string, fileName: string, content: string, existingFileId?: string): Promise<string | null> {
    const token = await this.ensureValidToken();
    if (!token) return null;

    try {
      const blob = new Blob([content], { type: 'application/json' });

      if (existingFileId) {
        // Update existing file
        const response = await fetch(
          `${GOOGLE_DRIVE_UPLOAD_API}/files/${existingFileId}?uploadType=media`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: blob,
          }
        );

        return response.ok ? existingFileId : null;
      } else {
        // Create new file
        const metadata = {
          name: fileName,
          parents: [folderId],
          mimeType: 'application/json',
        };

        const formData = new FormData();
        formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        formData.append('file', blob);

        const response = await fetch(
          `${GOOGLE_DRIVE_UPLOAD_API}/files?uploadType=multipart&fields=id`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (response.ok) {
          const result = await response.json();
          return result.id;
        }

        return null;
      }
    } catch (error) {
      console.error('GoogleDriveRepository: Error uploading file:', error);
      return null;
    }
  }

  /**
   * Delete a file or folder from Google Drive
   * Moves to trash (can be recovered by user if needed)
   */
  async deleteFileOrFolder(fileId: string): Promise<boolean> {
    const token = await this.ensureValidToken();
    if (!token) return false;

    try {
      // Move to trash instead of permanent delete (safer for user data)
      const response = await fetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ trashed: true }),
        }
      );

      if (response.ok) {
        return true;
      }

      console.error('GoogleDriveRepository: Failed to delete file:', response.status);
      return false;
    } catch (error) {
      console.error('GoogleDriveRepository: Error deleting file:', error);
      return false;
    }
  }

  /**
   * Delete an entity's content folder from Google Drive
   * e.g., "Addresses/Lone Peak Drive" or "Vehicles/2020 Toyota Camry"
   */
  async deleteEntityFolder(entityType: 'Addresses' | 'Vehicles' | 'Insurance' | 'Healthcare' | 'Financial', entityLabel: string): Promise<boolean> {
    if (!this.profileFolderId) return false;

    try {
      // Find the entity type folder (e.g., "Addresses")
      const entityTypeFolderId = await this.findFolder(this.profileFolderId, entityType);
      if (!entityTypeFolderId) {
        return false;
      }

      // Find the specific entity folder (e.g., "Lone Peak Drive")
      const entityFolderId = await this.findFolder(entityTypeFolderId, entityLabel);
      if (!entityFolderId) {
        return false;
      }

      // Delete the folder (moves to trash)
      const deleted = await this.deleteFileOrFolder(entityFolderId);
      if (deleted) {
        // Clear from folder cache
        const cacheKey = `${entityTypeFolderId}/${entityLabel}`;
        this.folderCache.delete(cacheKey);
      }
      return deleted;
    } catch (error) {
      console.error('GoogleDriveRepository: Error deleting entity folder:', error);
      return false;
    }
  }

  // ============================================================================
  // ADDRESSES
  // ============================================================================

  async getAddresses(_profileId: string): Promise<Address[]> {
    return this.cache.addresses;
  }

  async getAddress(_profileId: string, addressId: string): Promise<Address | null> {
    return this.cache.addresses.find(a => a.id === addressId) || null;
  }

  async saveAddress(_profileId: string, address: Address): Promise<void> {
    const now = new Date().toISOString();
    const updatedAddress = { ...address, updatedAt: now };

    const index = this.cache.addresses.findIndex(a => a.id === address.id);
    if (index >= 0) {
      this.cache.addresses[index] = updatedAddress;
    } else {
      this.cache.addresses.push(updatedAddress);
    }

    this.scheduleSave();
  }

  async deleteAddress(_profileId: string, addressId: string): Promise<void> {
    // Get the address before deleting to find its folder name
    const address = this.cache.addresses.find(a => a.id === addressId);
    if (address) {
      // Delete the entity folder from Google Drive (e.g., "Addresses/Lone Peak Drive")
      const folderName = address.label || address.street || address.city;
      if (folderName) {
        // Fire and forget - don't block on folder deletion
        this.deleteEntityFolder('Addresses', folderName).catch(err => {
          console.error('GoogleDriveRepository: Failed to delete address folder:', err);
        });
      }
    }

    this.cache.addresses = this.cache.addresses.filter(a => a.id !== addressId);
    this.scheduleSave();
  }

  // ============================================================================
  // VEHICLES
  // ============================================================================

  async getVehicles(_profileId: string): Promise<Vehicle[]> {
    return this.cache.vehicles;
  }

  async getVehicle(_profileId: string, vehicleId: string): Promise<Vehicle | null> {
    return this.cache.vehicles.find(v => v.id === vehicleId) || null;
  }

  async saveVehicle(_profileId: string, vehicle: Vehicle): Promise<void> {
    const now = new Date().toISOString();
    const updatedVehicle = { ...vehicle, updatedAt: now };

    const index = this.cache.vehicles.findIndex(v => v.id === vehicle.id);
    if (index >= 0) {
      this.cache.vehicles[index] = updatedVehicle;
    } else {
      this.cache.vehicles.push(updatedVehicle);
    }

    this.scheduleSave();
  }

  async deleteVehicle(_profileId: string, vehicleId: string): Promise<void> {
    // Get the vehicle before deleting to find its folder name
    const vehicle = this.cache.vehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      // Delete the entity folder from Google Drive (e.g., "Vehicles/2020 Toyota Camry")
      const folderName = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
      if (folderName) {
        this.deleteEntityFolder('Vehicles', folderName).catch(err => {
          console.error('GoogleDriveRepository: Failed to delete vehicle folder:', err);
        });
      }
    }

    this.cache.vehicles = this.cache.vehicles.filter(v => v.id !== vehicleId);
    this.scheduleSave();
  }

  // ============================================================================
  // HEALTHCARE
  // ============================================================================

  async getHealthProviders(_profileId: string): Promise<HealthProvider[]> {
    return this.cache.health;
  }

  async getHealthProvider(_profileId: string, providerId: string): Promise<HealthProvider | null> {
    return this.cache.health.find(h => h.id === providerId) || null;
  }

  async saveHealthProvider(_profileId: string, provider: HealthProvider): Promise<void> {
    const now = new Date().toISOString();
    const updatedProvider = { ...provider, updatedAt: now };

    const index = this.cache.health.findIndex(h => h.id === provider.id);
    if (index >= 0) {
      this.cache.health[index] = updatedProvider;
    } else {
      this.cache.health.push(updatedProvider);
    }

    this.scheduleSave();
  }

  async deleteHealthProvider(_profileId: string, providerId: string): Promise<void> {
    // Get the provider before deleting to find its folder name
    const provider = this.cache.health.find(h => h.id === providerId);
    if (provider) {
      // Delete the entity folder from Google Drive (e.g., "Healthcare/Dr. Smith")
      const folderName = provider.name;
      if (folderName) {
        this.deleteEntityFolder('Healthcare', folderName).catch(err => {
          console.error('GoogleDriveRepository: Failed to delete health provider folder:', err);
        });
      }
    }

    this.cache.health = this.cache.health.filter(h => h.id !== providerId);
    this.scheduleSave();
  }

  // ============================================================================
  // FINANCIAL
  // ============================================================================

  async getFinancialInstitutions(_profileId: string): Promise<FinancialInstitution[]> {
    return this.cache.financial;
  }

  async getFinancialInstitution(_profileId: string, institutionId: string): Promise<FinancialInstitution | null> {
    return this.cache.financial.find(f => f.id === institutionId) || null;
  }

  async saveFinancialInstitution(_profileId: string, institution: FinancialInstitution): Promise<void> {
    const now = new Date().toISOString();
    const updatedInstitution = { ...institution, updatedAt: now };

    const index = this.cache.financial.findIndex(f => f.id === institution.id);
    if (index >= 0) {
      this.cache.financial[index] = updatedInstitution;
    } else {
      this.cache.financial.push(updatedInstitution);
    }

    this.scheduleSave();
  }

  async deleteFinancialInstitution(_profileId: string, institutionId: string): Promise<void> {
    // Get the institution before deleting to find its folder name
    const institution = this.cache.financial.find(f => f.id === institutionId);
    if (institution) {
      // Delete the entity folder from Google Drive (e.g., "Financial/Chase Bank")
      const folderName = institution.name;
      if (folderName) {
        this.deleteEntityFolder('Financial', folderName).catch(err => {
          console.error('GoogleDriveRepository: Failed to delete financial institution folder:', err);
        });
      }
    }

    this.cache.financial = this.cache.financial.filter(f => f.id !== institutionId);
    this.scheduleSave();
  }

  // ============================================================================
  // INSURANCE
  // ============================================================================

  async getInsurancePolicies(_profileId: string): Promise<InsurancePolicy[]> {
    return this.cache.insurance;
  }

  async getInsurancePolicy(_profileId: string, policyId: string): Promise<InsurancePolicy | null> {
    return this.cache.insurance.find(i => i.id === policyId) || null;
  }

  async saveInsurancePolicy(_profileId: string, policy: InsurancePolicy): Promise<void> {
    const now = new Date().toISOString();
    const updatedPolicy = { ...policy, updatedAt: now };

    const index = this.cache.insurance.findIndex(i => i.id === policy.id);
    if (index >= 0) {
      this.cache.insurance[index] = updatedPolicy;
    } else {
      this.cache.insurance.push(updatedPolicy);
    }

    this.scheduleSave();
  }

  async deleteInsurancePolicy(_profileId: string, policyId: string): Promise<void> {
    // Get the policy before deleting to find its folder name
    const policy = this.cache.insurance.find(i => i.id === policyId);
    if (policy) {
      // Delete the entity folder from Google Drive (e.g., "Insurance/State Farm Auto")
      const folderName = policy.company || policy.policyNumber;
      if (folderName) {
        this.deleteEntityFolder('Insurance', folderName).catch(err => {
          console.error('GoogleDriveRepository: Failed to delete insurance policy folder:', err);
        });
      }
    }

    this.cache.insurance = this.cache.insurance.filter(i => i.id !== policyId);
    this.scheduleSave();
  }

  // ============================================================================
  // PORTALS
  // ============================================================================

  async getPortals(_profileId: string): Promise<Portal[]> {
    return this.cache.portals;
  }

  async savePortals(_profileId: string, portals: Portal[]): Promise<void> {
    this.cache.portals = portals;
    this.scheduleSave();
  }

  // ============================================================================
  // SYNC OPERATIONS
  // ============================================================================

  async sync(_profileId: string): Promise<SyncStatus> {
    // Force save any pending changes first
    if (this.isDirty) {
      await this.forceSave();
    }

    // Reload from Drive
    await this.loadProfileFromDrive();

    return {
      isSynced: true,
      lastSyncedAt: this.meta.lastSyncedAt,
      pendingChanges: this.isDirty,
    };
  }

  hasPendingChanges(_profileId: string): boolean {
    return this.isDirty;
  }

  async flushPendingChanges(_profileId: string): Promise<void> {
    await this.forceSave();
  }

  /**
   * Get the root folder URL for user to open in Google Drive
   */
  getRootFolderUrl(): string | null {
    if (!this.rootFolderId) return null;
    return `https://drive.google.com/drive/folders/${this.rootFolderId}`;
  }

  /**
   * Get the profile folder URL for user to browse their files
   */
  getProfileFolderUrl(): string | null {
    if (!this.profileFolderId) return null;
    return `https://drive.google.com/drive/folders/${this.profileFolderId}`;
  }
}

// Export singleton instance
export const googleDriveRepository = new GoogleDriveRepository();
